import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { RotateCcwIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { SymptomField } from '../components/decoder/SymptomField';
import { AnswerPanel } from '../components/decoder/AnswerPanel';
import { AskYourClinician } from '../components/decoder/AskYourClinician';
import { CtaBand } from '../components/ui/CtaBand';
import { useSymptomSelection } from '../hooks/useSymptomSelection';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function Symptoms() {
  const {
    selected,
    toggle,
    clear
  } = useSymptomSelection();
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Symptom decoder — ${brand.name}`,
    description: 'Select what you are feeling and get a plain-language read of what is happening in your body, what usually helps, and the exact questions to ask a clinician. Free, no email, no account.'
  });
  return <main className="w-full bg-cream" data-id="element-709" data-mp="3ki0qbf7ubftu">
      <PageHeader eyebrow="The symptom decoder" title="Tell us what you’re feeling. We’ll tell you what’s happening." intro="Pick anything that sounds like you — including the things you have never said out loud. Your answer builds as you go. Nothing is stored, nothing is sent, and you will never be asked for an email address to see it." data-id="element-710" data-mp="3pf0utlp1kaiu" />

      <section className="bg-cream" data-id="element-711" data-mp="77l51g1xna1o">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-10 md:px-8 md:pb-28 md:pt-14" data-id="element-712" data-mp="c88uhxaijits">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:gap-20" data-id="element-713" data-mp="gzqvglcjdjer">
            <div data-id="element-714" data-mp="lyamv19wfnei">
              <div className="mb-8 flex items-center justify-between gap-4" data-id="element-715" data-mp="lytrxhegahb4">
                <p className="text-sm text-ink-soft" aria-live="polite" data-id="element-716" data-mp="qxwyx7dzqxlq">
                  {selected.length === 0 ? 'Nothing selected yet' : `${selected.length} selected — take as long as you like`}
                </p>
                {selected.length > 0 && <button type="button" onClick={clear} className="inline-flex items-center gap-2 text-sm text-ink-soft transition-colors duration-150 ease-brand hover:text-brick" data-id="element-717" data-mp="2n11qqo50jfwa">
                    <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-718" data-mp="2w06npq0xf16c" />
                    Start over
                  </button>}
              </div>
              <SymptomField selected={selected} onToggle={toggle} data-id="element-719" data-mp="35q82fr7e378l" />
            </div>

            <div className="flex flex-col gap-10" data-id="element-720" data-mp="3jrc3y6as3vh6">
              <AnimatePresence mode="wait" initial={false} data-id="element-721" data-mp="3joy6jqsmm76g">
                {selected.length === 0 ? <EmptyState key="empty" data-id="element-722" data-mp="3fnmhpnhktpzb" /> : <AnswerPanel key="answer" selected={selected} data-id="element-723" data-mp="1pdjhzdbcqdsa" />}
              </AnimatePresence>

              {selected.length > 0 && <AskYourClinician selected={selected} data-id="element-724" data-mp="1b7hjehynpo6d" />}

              <div className="rounded-brand border border-ink/15 bg-cream-deep p-7 md:p-8" data-id="element-725" data-mp="1b8spiofkr80n">
                <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-726" data-mp="1f7t2hqbdfgfq">
                  What this is, and isn’t
                </p>
                <p className="mt-4 leading-relaxed text-ink-soft" data-id="element-727" data-mp="16gsqqzb8ql5n">
                  This is an education tool, not a diagnosis. Perimenopause is diagnosed clinically —
                  from your age, your cycle history and your symptoms, by a person. What this does is
                  give you the language and the questions so that conversation starts from somewhere
                  better than “I just feel off”.
                </p>
                <p className="mt-4 leading-relaxed text-ink-soft" data-id="element-728" data-mp="niy9xz3vf5cp">
                  Some symptoms here overlap with thyroid disease, iron deficiency, and other things
                  worth ruling out.{' '}
                  <Link to="/testing" className="text-brick underline-offset-4 hover:underline" data-id="element-729" data-mp="2humvwl8is86l">
                    That is what our testing is for.
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Nobody should have to earn an answer." intro="If you want a clinician to look at this properly, the next step is five questions, one at a time, and you can stop at any point. If you would rather just take your summary elsewhere — please do." data-id="element-730" data-mp="7ur1jvlinlwa" />
    </main>;
}
function EmptyState() {
  return <div className="rounded-brand border border-dashed border-ink/25 bg-cream-light p-10 md:p-14" data-id="element-731" data-mp="3q86y508vuuyw">
      <p className="font-serif text-3xl leading-snug text-ink md:text-4xl" data-id="element-732" data-mp="3laom30zkjzmo">
        Start anywhere. There is no wrong order.
      </p>
      <p className="mt-6 max-w-lg leading-relaxed text-ink-soft" data-id="element-733" data-mp="3c9h27nxsv1c4">
        Most women begin with sleep, or with the rage — the two that are hardest to say to a doctor
        in ten minutes. Select one and the explanation appears here straight away.
      </p>
      <p className="mt-6 max-w-lg leading-relaxed text-ink-soft" data-id="element-734" data-mp="1msbqw2sg6adn">
        You can select as many as you like. The more you pick, the more precise the summary you can
        take to an appointment.
      </p>
    </div>;
}